/**
 * Custom scripts for the subscription widget.
 *
 * - Moves the selling points inside the widget.
 * - Adds per-quantity prices to each subscription option.
 */

/**
 * Page load.
 */
document.addEventListener('DOMContentLoaded', () => {
  const container = document.querySelector('.recharge-subscription-widget')

  if (!container) {
    return
  }

  /**
   * Run on page load.
   */
  setSellingPoints()
  setPerQuantityPrices()
  setEventListeners()
  setSelectedFrequency()

  /**
   * Handles changes to the block content.
   */
  new MutationObserver(entries => {
    entries.forEach(entry => {
      /**
       * Parent load.
       * - Returns when the main subscription widget loads inside the block.
       */
      if (
        entry.target.parentElement.classList.contains(
          'recharge-subscription-widget'
        )
      ) {
        setSellingPoints()
        setPerQuantityPrices()
        setEventListeners()
        setSelectedFrequency()
        setTimeout(() => {
          document
            .querySelector('[name="rc_selling_plan"]')
            .addEventListener('change', () => {
              const url = new URL(window.location.href)
              const sellPlanId = url.searchParams.get('selling_plan')
              updateWindowURLWithSellPlanId(sellPlanId)
            })
        }, 100)
        return
      }

      /**
       * Price change.
       */
      if (entry.target.classList.contains('price-label')) {
        setPerQuantityPrices()
        return
      }
    })
  }).observe(container, {
    childList: true,
    subtree: true
  })
})

/**
 * Adds per-quantity pricing to each subscription option.
 */
function setPerQuantityPrices () {
  const options = document.querySelectorAll('.rc-radio')

  options.forEach(element => {
    const priceElement = element.querySelector('.price-label')

    if (priceElement) {
      setPerQuantityPrice(element)
    }
  })
}

/**
 * Sets the per-quantity price for the subscription option.
 * @param {HTMLElement} element - The subscription option element.
 */
function setPerQuantityPrice (element) {
  const discountLabelElement = element.querySelector('.discount-label')
  const perQuantityElement = element.querySelector('.per-quantity')
  const value = getPerQuantityString(element)

  /**
   * If the element already exists, update it.
   */
  if (perQuantityElement) {
    if (value) {
      perQuantityElement.innerHTML = value
    }

    return
  }

  /**
   * Else, create the element and inject it.
   */
  if (value && discountLabelElement) {
    discountLabelElement.innerHTML = `
      ${discountLabelElement.innerHTML} <span class="per-quantity">${value}</span>
    `
  }
}

/**
 * Returns the per-quantity string for the subscription option element.
 * @param {HTMLElement} element - The subscription option element.
 * @returns {String}
 */
function getPerQuantityString (element) {
  const quantity = getSubscriptionQuantity()
  const priceElement = element.querySelector('.price-label')
  const currency = theme.localization.country.currency.symbol

  if (priceElement) {
    const price = Number(priceElement.textContent.replace(currency, 0))

    return `(${currency}${(price / quantity).toFixed(2)} ea.)`
  }

  return null
}

/**
 * Returns the subscription quantity.
 * @returns {Number}
 */
function getSubscriptionQuantity () {
  const quantityElement = document.querySelector(
    'input[name="Pack Size"]:checked'
  )

  if (!quantityElement) {
    return 1
  }

  return Number(quantityElement.value.split('-')[0])
}

/**
 * Moves the selling points into the subscription widget.
 */
function setSellingPoints () {
  const sellingPoints = document.getElementById('recharge-extra-text')
  const options = document.querySelector('.rc-radio-group__options')

  /**
   * Returns if the element exists.
   */
  if (options && options.querySelector('#recharge-extra-text')) {
    return
  }

  if (options && sellingPoints) {
    options.appendChild(sellingPoints)
  }
}

/**
 * Updates the current window URL by appending or modifying the 'selling_plan' query parameter.
 * This function also attempts to update the selling plan in the first available widget's state.
 * 
 * @param {string|number} sellPlanId - The selling plan identifier to be set in the URL and widget.
 */
function updateWindowURLWithSellPlanId(sellPlanId) {
  // Validate sellPlanId to ensure it is either a string or a number
  if (typeof sellPlanId !== 'string' && typeof sellPlanId !== 'number') {
    console.error('Invalid sellPlanId:', sellPlanId);
    return;
  }

  // Create a URL object based on the current window location
  const url = new URL(window.location.href);
  // Set or replace the 'selling_plan' query parameter
  url.searchParams.set('selling_plan', sellPlanId);
  // Update the browser history state with the new URL
  window.history.replaceState(null, '', url.toString());

  // Try updating the selling plan in the first widget found
  try {
    const widget = Object.values(window.Recharge.widgets)[0];
    if (widget && widget.state) {
      widget.state.sellingPlan = sellPlanId;
    } else {
      throw new Error('Widget or widget state not accessible.');
    }
  } catch (error) {
    console.error('Error setting widget selling plan:', error);
  }
}

/**
 * Sets the selling plan based on the selected pack size from radio buttons.
 * This function updates both the selling plan input element and the corresponding widget state.
 */
function setSelectedFrequency() {
  // Locate the frequency select element in the document
  const frequencySelect = document.querySelector('[name="rc_selling_plan"]');
  if (!frequencySelect) {
    console.warn('Frequency select element not found.');
    return;
  }

  // Iterate over each pack size radio button to find the checked one
  document.querySelectorAll('[name="Pack Size"]').forEach((element, index) => {
    if (element.checked) {
      // Set the corresponding index in the select element to match the checked radio button
      frequencySelect.selectedIndex = index;
      // Get the plan ID from the selected option in the dropdown
      const frequencyPlanId = parseInt(frequencySelect.options[index].value, 10);

      // Validate the derived frequency plan ID
      if (isNaN(frequencyPlanId)) {
        console.error('Invalid frequency plan ID derived from selection.');
        return;
      }

      // Attempt to update the selling plan in the first widget's state
      try {
        const widget = Object.values(window.Recharge.widgets)[0];
        if (widget && widget.state) {
          widget.state.sellingPlan = frequencyPlanId;
        } else {
          throw new Error('Widget or widget state not accessible.');
        }
      } catch (error) {
        console.error('Error updating widget selling plan:', error);
        return;
      }

      // Set the selected option in the dropdown and update the selling plan input element
      frequencySelect.options[index].selected = true;
      const sellingPlanInput = document.querySelector('[name="selling_plan"]');
      if (sellingPlanInput) {
        sellingPlanInput.value = frequencyPlanId;
      } else {
        console.warn('Selling plan input element not found.');
      }

      // Set a timeout to ensure the UI updates before executing further actions
      setTimeout(() => {
        frequencySelect.options[index].selected = true;
        // Update the window URL with the selected selling plan ID
        updateWindowURLWithSellPlanId(frequencyPlanId);
      }, 100);
    }
  });
}



/**
 * Sets event listeners.
 */
function setEventListeners () {
  /**
   * Sets the selected frequency when the options are selected.
   */
  [
    ...document.querySelectorAll('[name="Pack Size"]'),
    ...document.querySelectorAll('[name="purchaseOption"]')
  ].forEach(element => {
    element.addEventListener('change', () => {
      setSelectedFrequency()
    })
  })

}
